import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { listScmBindings } from './api'
import type { ScmRepoBinding } from './types'
import useLoginStore from '@/store/LoginStore'

/**
 * The workspace's repo→project bindings, keyed by project id. Listing is readable by any member
 * (only writes require ManageWorkspaceSettings), so task-level surfaces can use this to decide
 * whether a task's project has GitHub wired up at all. Cached like identities — bindings change
 * rarely and only from workspace settings.
 */
export function useScmBindings() {
  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)

  const { data, isLoading } = useQuery({
    queryKey: ['scm-bindings', workspaceSlug],
    queryFn: () => listScmBindings(workspaceSlug as string),
    enabled: !!workspaceSlug,
    staleTime: 5 * 60 * 1000,
  })

  const byProjectId = useMemo(() => {
    const map = new Map<string, ScmRepoBinding>()
    for (const binding of data ?? []) map.set(binding.projectId, binding)
    return map
  }, [data])

  return { byProjectId, isLoading: !!workspaceSlug && isLoading }
}

/**
 * The binding for a single project, or null when the project has no repo bound (or the task is
 * in the Inbox / a personal space and has no project at all).
 */
export function useScmBinding(projectId: string | null | undefined) {
  const { byProjectId, isLoading } = useScmBindings()
  return {
    binding: projectId ? byProjectId.get(projectId) ?? null : null,
    isLoading,
  }
}
